import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink, MessageCircle } from 'lucide-react';

import { ContactSection } from '@/components/contact-section';
import { PublicSiteHeader } from '@/components/public-site-header';
import { canonicalSiteUrl } from '@/config';

import { whatsappUrlEn as whatsappUrl } from '@/lib/whatsapp';

const portfolioCases = [
  {
    title: 'Man Demolition',
    slug: 'man-demolition',
    category: 'Demolition service website',
    url: 'https://mandemolition.com/',
    image: '/images/case-man-demolition.webp',
    alt: 'Man Demolition website homepage showing residential, office and shop demolition services in Hong Kong',
    body: 'A Hong Kong demolition service website covering residential, office, shop and restaurant clearance. The structure puts service scope, transparent quoting and a direct WhatsApp enquiry path within easy reach.',
  },
  {
    title: 'Man Aircon',
    slug: 'man-aircon',
    category: 'Local service website',
    url: 'https://www.manaircon.xyz/',
    image: '/images/case-man-aircon.webp',
    alt: 'Man Aircon website screenshot showing an air-conditioning service website design',
    body: 'A service website for air-conditioning installation, repair, cleaning and maintenance. Useful as a reference if your business needs clear service areas, project examples, reviews and a direct WhatsApp enquiry path.',
  },
  {
    title: 'Man Plumber',
    slug: 'man-plumber',
    category: 'Repair service website',
    url: 'https://manplumber.com/',
    image: '/images/case-man-plumber.webp',
    alt: 'Man Plumber website screenshot showing an urgent plumbing repair service website',
    body: 'An emergency plumbing repair website built around fast mobile enquiries. A good direction for repair, maintenance and on-site service businesses that need visitors to understand scope, pricing direction and contact options quickly.',
  },
  {
    title: 'Shu Yee Engineering',
    slug: 'shu-yee-engineering',
    category: 'Company website refresh',
    url: 'https://www.shuyee.xyz/',
    image: '/images/case-shuyee.webp',
    alt: 'Shu Yee Engineering website screenshot showing a company website refresh',
    body: 'A company website and content structure refresh. Suitable for businesses with scattered old content that need to reorganise company positioning, service introductions and mobile reading flow.',
  },
  {
    title: 'PLUS EV',
    slug: 'plus-ev',
    category: 'EV service website',
    url: 'https://plusevhk.com/',
    image: '/images/case-plus-ev.webp',
    alt: 'PLUS EV website screenshot showing an electric vehicle service website',
    body: 'A website for an EV-related service brand. A useful reference for new services that need to explain the offer clearly before using the site to receive search, ads or social media enquiries.',
  },
];

export const Route = createFileRoute('/en_/cases')({
  head: () => {
    const pageUrl = `${canonicalSiteUrl}/en/cases`;
    const zhUrl = `${canonicalSiteUrl}/cases`;
    const ogImageUrl = `${canonicalSiteUrl}/og-image.jpg`;
    const title = 'Website Portfolio | Doq Creative Hong Kong';
    const description =
      'View real website projects by Doq Creative, including local service websites, company website refreshes and landing pages for Hong Kong businesses.';

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: pageUrl },
        { property: 'og:image', content: ogImageUrl },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:image:alt', content: 'Doq Creative Hong Kong web design and SEO preview' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImageUrl },
        {
          'script:ld+json': {
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'CollectionPage',
                '@id': `${pageUrl}#collection`,
                name: 'Website Portfolio',
                url: pageUrl,
                inLanguage: 'en',
                isPartOf: { '@type': 'WebSite', '@id': `${canonicalSiteUrl}/#website` },
                mainEntity: portfolioCases.map((caseItem) => ({
                  '@type': 'CreativeWork',
                  name: caseItem.title,
                  url: caseItem.url,
                  image: `${canonicalSiteUrl}${caseItem.image}`,
                  description: caseItem.body,
                })),
              },
              {
                '@type': 'BreadcrumbList',
                '@id': `${pageUrl}#breadcrumb`,
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'Home', item: `${canonicalSiteUrl}/en` },
                  { '@type': 'ListItem', position: 2, name: 'Cases', item: pageUrl },
                ],
              },
            ],
          },
        },
      ],
      links: [
        { rel: 'canonical', href: pageUrl },
        { rel: 'alternate', hrefLang: 'en', href: pageUrl },
        { rel: 'alternate', hrefLang: 'zh-HK', href: zhUrl },
        { rel: 'alternate', hrefLang: 'x-default', href: zhUrl },
      ],
    };
  },
  component: EnglishCasesPage,
});

function EnglishCasesPage() {
  return (
    <div className="min-h-screen bg-[#f4efe4] text-[#102d28]" style={{ overflowAnchor: 'none' }}>
      <div className="mx-auto max-w-[1540px] border-x border-[#143d35]/16 bg-[#f8f3e9] shadow-[0_0_0_1px_rgba(20,61,53,0.04)]">
        <PublicSiteHeader locale="en" active="cases" enHref="/en/cases" zhHref="/cases" />

        <main id="main-content">
          <section className="relative overflow-hidden border-b border-[#143d35]/14 px-5 py-16 sm:px-8 lg:px-24 lg:py-20">
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(20,61,53,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(20,61,53,0.07)_1px,transparent_1px)] bg-[size:58px_58px]" />
            <div className="relative z-10 max-w-4xl">
              <Link to="/en" className="inline-flex items-center gap-2 text-sm font-semibold text-[#173d35] hover:text-[#815321]">
                <ArrowLeft className="size-4" />
                Back to English home
              </Link>
              <p className="mt-12 text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Selected work</p>
              <h1 className="mt-5 max-w-3xl text-balance text-4xl font-semibold leading-[1.12] tracking-[-0.06em] text-[#063d36] sm:text-5xl lg:text-6xl">
                Real website references for Hong Kong service businesses
              </h1>
              <p className="mt-8 max-w-2xl text-lg leading-9 tracking-[-0.02em] text-[#324740]">
                These projects show different ways to present services, trust signals, mobile enquiry buttons and campaign pages. If one feels close to your business, send it to us and we can map out what a similar website would need.
              </p>
            </div>
          </section>

          <section className="border-b border-[#143d35]/14 px-5 py-12 sm:px-8 lg:px-24">
            <div className="grid gap-6 md:grid-cols-2">
              {portfolioCases.map((item) => (
                <article key={item.title} className="group border border-[#143d35]/14 bg-[#fbf7ef]">
                  <a
                    href={`/en/case/${item.slug}`}
                    aria-label={`View ${item.title} case study`}
                    className="block overflow-hidden bg-[#efe8dc]"
                  >
                    <img
                      src={item.image}
                      alt={item.alt}
                      width="1400"
                      height="900"
                      className="aspect-[16/10] w-full object-cover object-top transition duration-500 group-hover:scale-[1.015]"
                      loading="eager"
                    />
                  </a>
                  <div className="p-7 sm:p-9">
                    <p className="text-sm font-semibold text-[#815321]">{item.category}</p>
                    <div className="mt-3 flex items-start justify-between gap-4">
                      <a href={`/en/case/${item.slug}`} className="text-3xl font-semibold tracking-[-0.055em] text-[#102d28] hover:text-[#815321]">{item.title}</a>
                      <a
                        href={`/en/case/${item.slug}`}
                        className="grid size-10 shrink-0 place-items-center border border-[#143d35]/18 text-[#143d35] transition hover:bg-[#063d36] hover:text-white"
                        aria-label={`View ${item.title} case study`}
                      >
                        <ExternalLink className="size-4" />
                      </a>
                    </div>
                    <p className="mt-5 max-w-2xl text-sm leading-7 text-[#344840]">{item.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <ContactSection
            locale="en"
            title="Want a similar website for your business?"
            description="Send us a reference, your current website, or a short note about the service you want to promote. We will suggest whether to start with a company website, a service page, SEO planning or a landing page."
          />
        </main>
        <footer className="border-t border-[#143d35]/14 px-5 py-6 text-center text-sm text-[#344840] sm:px-8 lg:px-24"><div className="flex flex-wrap justify-center gap-x-5 gap-y-2"><Link to="/en" className="font-semibold">Doq Creative Limited</Link><Link to="/en/terms">Terms of Service</Link><Link to="/en/privacy">Privacy Policy</Link></div><p className="mt-3">© 2026 Doq Creative Limited. All rights reserved.</p></footer>
      </div>
      <FloatingWhatsApp />
    </div>
  );
}

function FloatingWhatsApp() {
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-5 right-5 z-50 grid size-14 place-items-center rounded-full bg-[#063d36] text-[#fbf5ea] shadow-[0_16px_34px_rgba(6,61,54,0.28)] transition hover:-translate-y-1 hover:bg-[#0b4f45]"
      aria-label="WhatsApp enquiry"
    >
      <MessageCircle className="size-6" strokeWidth={1.8} />
    </a>
  );
}
